import { Component, OnDestroy, OnInit } from '@angular/core';
import { timer, forkJoin, Subscription } from 'rxjs';
import { ErisTableDataSource } from 'src/app/models/components/eris-table';
import { TransfersFilesStatus } from 'src/app/models/services/transfersFilesStatus.enum';
import { AudiencesStatus } from 'src/app/models/services/audiencesStatus.enum';
import { PublicisAudience } from 'src/app/models/services/admin/dataTransferInfo';
import { ErisManualProcessInfo } from 'src/app/models/services/admin/erisManualProcessInfo';
import { ErisProcessQueueInfo } from 'src/app/models/services/admin/erisProcessQueueInfo';
import { AdminService } from 'src/app/services/admin.service';
import { CompaniesService } from 'src/app/services/companies.service';
import { DialogService } from 'src/app/services/core/dialog.service';
import { MonitorService } from 'src/app/services/monitor.service';
import { UsersService } from 'src/app/services/users.service';
import { ThemeOptions } from 'src/app/theme-options';
import { AudiencesService } from 'src/app/services/audiences/audiences.service';
import { Audience, Transfer, TransferFile } from 'src/app/models/services/audience';
import { TransfersService } from 'src/app/services/audiences/transfers.service';
import { MenuCloseReason } from '@angular/material/menu/menu';

@Component({
  selector: 'manual-processes-block',
  templateUrl: './manual-processes-block.component.html',
  styleUrls: ['./manual-processes-block.component.sass']
})
export class ManualProcessesBlockComponent implements OnInit, OnDestroy {
  loadFinished: boolean = false;
  refreshTimerSubscription: Subscription = Subscription.EMPTY;
  loadManualProcessesSubscription: Subscription = Subscription.EMPTY;
  activePolling: boolean = true;

  manualProcessesInfo: ErisManualProcessInfo[]  = [];
  attributesQueueProcessesInfo: ErisProcessQueueInfo[] = [];
  audiencesDataSource: ErisTableDataSource = {
    tableDefinition: [
      {
        headerDescription: 'ID',
        enableSort: true,
        sortFieldName: 'id'
      }, {
        headerDescription: 'Company',
        enableSort: true,
        sortFieldName: 'company.id'
      }, {
        headerDescription: 'Audience',
        enableSort: true,
        sortFieldName: 'name'
      }, {
        headerDescription: 'Created',
        enableSort: true,
        sortFieldName: 'created'      
      }, {
        headerDescription: 'Refreshed',
        enableSort: true,
        sortFieldName: 'updated'      
      }, {
        headerDescription: 'Eris IDs',
        enableSort: true,
        sortFieldName: 'profilesCount'      
      }, {
        headerDescription: '',
        enableSort: false      
      }, {
        headerDescription: 'Status',
        enableSort: true,
        sortFieldName: 'status',
        headerClass: 'text-center'      
      }, {
        headerDescription: '',
        enableSort: false      
      }, {
        headerDescription: '',
        enableSort: false      
      }     
    ],
    data: [],
    enableSort: true,
    enablePagination: true,
    pageSize: 10
  };

  transfersDataSource: ErisTableDataSource = {
    tableDefinition: [{
        headerDescription: 'Company',
        enableSort: true,
        sortFieldName: 'audience.company.id',
      },{
        headerDescription: 'Target platform',
        enableSort: true,
        sortFieldName: 'targetPlatformId',
      }, {
        headerDescription: 'ID',
        enableSort: true,
        sortFieldName: 'id'
      }, {
        headerDescription: 'Name',
        enableSort: true,
        sortFieldName: 'name'
      }, {
        headerDescription: 'Audience',
        enableSort: true,
        sortFieldName: 'audience.id'
      }, {
        headerDescription: 'Created',
        enableSort: true,
        sortFieldName: 'created'
      }, {
        headerDescription: 'Scheduling',
        enableSort: true,
        sortFieldName: 'scheduling.name'
      }, {
        headerDescription: 'Attributes',
        enableSort: false
      }, {
        headerDescription: 'Identifiers',
        enableSort: false,
        headerClass: 'text-center'
      }, {
        headerDescription: '', // Show files button
        enableSort: false
      }, {
        headerDescription: '', // Actions menu button
        enableSort: false
      }
      ] ,
    data: [],
    enableSort: true,
    enablePagination: true,
    pageSize: 10
  };

  public filesVisibleAudienceId?: number;  

  constructor(
    public usersService: UsersService,
    public companiesService: CompaniesService,
    public audiencesService: AudiencesService,
    public transfersService: TransfersService,
    private globals: ThemeOptions,
    private adminService: AdminService,
    private monitorService: MonitorService,
    private dialogService: DialogService
  ) { 
  }

  ngOnInit(): void {
    this.globals.adminMode = true;
    this.loadManualProcessesInfo();    
  }

  ngOnDestroy(): void {
    this.stopRefreshPolling();
    this.globals.adminMode = false;
  }

  loadManualProcessesInfo = (): void => {
    console.log("loadManualProcessesInfo start")
    let _this: ManualProcessesBlockComponent = this;
    this.loadManualProcessesSubscription = forkJoin([
      this.monitorService.getManualProcessesInfo(),
      this.monitorService.getRunningAndFailedAudiences(),
      this.monitorService.getRunningAndFailedTransfers()
    ]).subscribe({
      next(resp: [ErisManualProcessInfo[], Audience[], Transfer[]]) {
        _this.manualProcessesInfo = resp[0];
        
        let cloned: ErisTableDataSource = { ..._this.audiencesDataSource};
        cloned.data = resp[1];
        _this.audiencesDataSource = cloned;

        let cloned2: ErisTableDataSource = { ..._this.transfersDataSource};
        cloned2.data = resp[2];
        _this.transfersDataSource = cloned2;

        _this.loadFinished = true;
        _this.startRefreshPolling();
        console.log("loadManualProcessesInfo next");     
      },
      error(err) { 
        _this.startRefreshPolling();
        console.log("loadManualProcessesInfo error")     
      },
      complete() { 
      }
    });        
  }

  getStatusStyle = (audience: PublicisAudience): string => {
    switch (audience?.status) {
      case AudiencesStatus.Created:
        return "pe-7s-check";
      case AudiencesStatus.Error:
        return "pe-7s-attention";
      case AudiencesStatus.Archived:
        return "pe-7s-box1";  
      default:
        return "pe-7s-hourglass fa-pulse";
    };
  }

  getStatusLabel = (audience: PublicisAudience): string => {
    switch (audience?.status) {
      case AudiencesStatus.Created:
        return "Done";
      case AudiencesStatus.Error:
        return "Error";
      case AudiencesStatus.CalculatingProfilesCount:
        return "Calculating profiles count";
      case AudiencesStatus.CalculatingProfilesCountPerType:
        return "Calculating profiles count per type";
      case AudiencesStatus.RetrievingProfiles:
        return "Retrieving profiles";
      case AudiencesStatus.CalculatingLookAlikeParameters:
        return "Calculating look-alike parameters";        
      case AudiencesStatus.MovingAudience:
        return "Moving audience";          
      case AudiencesStatus.WaitingRetry:
        return "Processing issue, waiting for retry";            
      case AudiencesStatus.Archived:
        return "Archived";              
      default:
        return `Creating (${audience?.progressPercentage}%)`;
    };
  }

  getFileStatusStyle = (audienceFile: TransferFile): string => {
    switch (audienceFile?.status) {
      case TransfersFilesStatus.Creating:
      case TransfersFilesStatus.Sending:
      case TransfersFilesStatus.Sharing:
      case TransfersFilesStatus.WaitingCreationWindow:
        return "pe-7s-hourglass fa-pulse";
      case TransfersFilesStatus.CreationError:
      case TransfersFilesStatus.SentError:
      case TransfersFilesStatus.SharingError:
        return "pe-7s-attention";
      default:
        return "pe-7s-check";
    }    
  }

  getFileStatusLabel = (audienceFile: TransferFile): string => {
    switch (audienceFile?.status) {
      case TransfersFilesStatus.Creating:
        return `Creating (${audienceFile?.progress}%)`;
      case TransfersFilesStatus.Sending:
        return `Sending (${audienceFile?.progress}%)`;
      case TransfersFilesStatus.CreationError:
        return "Creation error";
      case TransfersFilesStatus.SentError:
        return "Sent error";
      case TransfersFilesStatus.Sharing:
        return "Sharing";
      case TransfersFilesStatus.SharingError:
        return "Sharing error";
      case TransfersFilesStatus.WaitingCreationWindow:
        return "Waiting for creation window";
      default:
        return "Done";
    }
  }

  refreshAudience = (audience: PublicisAudience): void => {
    let _this: ManualProcessesBlockComponent = this;
    this.stopRefreshPolling();
    this.dialogService.showConfirmation(
      `Are you sure to refresh "${audience.name}"? The look-alikes information related with the audience will be removed.`,
      () => {
        audience.status = AudiencesStatus.CalculatingProfilesCount;
        
        if(!audience.id || !audience.company || !audience.company.id)
          return;

        _this.adminService.refreshAudience(audience.id, audience.company.id).subscribe(
          () => {
            _this.dialogService.showMessage("Refresh audience", "Audience refresh process launched");
            _this.loadManualProcessesInfo();
          }, () => {
            _this.loadManualProcessesInfo();
          }, () => {
          });    
      },
      () => {
      });    
  }  

  deleteAudience = (audience: PublicisAudience): void => {
    let _this: ManualProcessesBlockComponent = this;
    this.stopRefreshPolling();
    this.dialogService.showConfirmation(
      `Are you sure to remove "${audience.name}"?`,
      () => {
        if(!audience.id || !audience.company || !audience.company.id)
          return;

        _this.adminService.removeAudience(audience.id, audience.company.id).subscribe(
          () => {
            let index: number = _this.audiencesDataSource.data.findIndex(x => x.id == audience.id);
            let cloned: ErisTableDataSource = { ..._this.audiencesDataSource};
            cloned.data.splice(index, 1);
            _this.audiencesDataSource = cloned;            
            _this.dialogService.showMessage("Remove audience", "Audience removed");
            _this.loadManualProcessesInfo();
          }, () => {
            _this.loadManualProcessesInfo();
          }, () => {
          });  
      }, 
      () => {},
      true
    );
  }  

  getTransferFileIcon = (transfer: Transfer): string => {    
    return `assets/images/target-platforms/${transfer.targetPlatform}.png`;
  }

  deleteTransferFile = (transfer: Transfer, file: TransferFile): void => {
    this.stopRefreshPolling();
    this.dialogService.showConfirmation(`Are you sure to remove file  "${file.id}"?`, 
      () => {
        if(!transfer.audience || !transfer.audience.company || !transfer.audience.id || !transfer.id || !file.id || !transfer.audience.company.id)
          return;

        this.adminService.removeTransferFile(transfer.audience.id, transfer.id, file.id, transfer.audience.company.id).subscribe(
          () => {
            if (!transfer.files)
              return;

            let fileIndex: number = transfer.files.findIndex(x => x.id == file.id);
            transfer.files.splice(fileIndex, 1);
            this.loadManualProcessesInfo();
          });
      },
      () => { 
        this.loadManualProcessesInfo();
      });
  }  

  resendTransferFile = (transfer: Transfer, file: TransferFile): void => {
    this.dialogService.showConfirmation(`Are you sure to resend file  "${file.id}"?`, 
      () => {
        if(!transfer.audience || !transfer.audience.company || !transfer.audience.id || !transfer.id || !file.id || !transfer.audience.company.id)
          return;

        this.adminService.resendTransferFile(transfer.audience.id, transfer.id, file.id, transfer.audience?.company?.id).subscribe(
          () => {
            this.dialogService.showMessage("Transfer file resent", "Transfer file resent");
          });
      },
      () => { });    
  }

  createAndSendFile = (transfer: Transfer): void => {
    let _this: ManualProcessesBlockComponent = this;
    this.dialogService.showConfirmation(
      `Are you sure to create and send file for transfer "${transfer.name}"?`,
      () => {
        _this.adminService.sendTransfer(transfer).subscribe(
          () => {
            _this.dialogService.showMessage(
              "Transfer file",
              "Transfer file created and sent successfully"
            );
          },
          () => {
          }
        );
      },
      () => {}
    );
  };

  onMenuOpenedInternal = (): void => {
    this.stopRefreshPolling();
  };

  onMenuClosedInternal = (reason: MenuCloseReason): void => {
    if (!reason) this.startRefreshPolling();
  };

  startRefreshPolling = (): void => {
    this.stopRefreshPolling();
    this.activePolling = true;
    this.refreshTimerSubscription = timer(10000).subscribe(() => this.loadManualProcessesInfo());
    console.debug("startRefreshPolling executed");
  };

  stopRefreshPolling = (): void => {
    if (!!this.refreshTimerSubscription) this.refreshTimerSubscription.unsubscribe();
    if (!!this.loadManualProcessesSubscription) this.loadManualProcessesSubscription.unsubscribe();

    this.activePolling = false;
    console.debug("stopRefreshPolling executed");
  };
}
